/**
 * Leesbare weergave van een notitie die uit een mail komt: eigen tekst voorop,
 * handtekening en eerdere berichten ingeklapt. Zie utils/mailTekst.js.
 */
import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { isMailNotitie, splitsMail, splitsGesprek, haalBijlagen } from '../utils/mailTekst';

const regelSx = { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55, fontSize: '0.875rem' };

function Inklap({ label, tekst, standaardOpen = false }) {
  const [open, setOpen] = useState(standaardOpen);
  if (!tekst) return null;
  return (
    <Box sx={{ mt: 0.75 }}>
      <Button size="small" onClick={() => setOpen((v) => !v)}
        sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: 12, color: '#64748b', fontWeight: 600 }}>
        {open ? '▾' : '▸'} {label}
      </Button>
      {open && (
        <Typography component="div" sx={{ ...regelSx, mt: 0.5, pl: 1.5, borderLeft: '2px solid #e2e8f0', color: '#64748b', fontSize: '0.8rem' }}>
          {tekst}
        </Typography>
      )}
    </Box>
  );
}

// Alleen handtekening + oud bericht (leeg doorgestuurd zonder FW-onderwerp): het oude bericht is de inhoud.
function metTerugval(s) {
  if (!s.eigen && !s.doorgestuurd && s.citaat) return { ...s, eigen: s.citaat, citaat: '' };
  return s;
}

function Bericht({ kopregel, eigen, handtekening, citaat, doorgestuurd, bijlagen = [] }) {
  const aantalEerder = (citaat.match(/^\s*(van|from):/gim) || []).length || (citaat ? 1 : 0);
  return (
    <Box sx={{ mb: 1 }}>
      {kopregel && <Typography sx={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 700, mb: 0.25 }}>{kopregel}</Typography>}
      {(eigen || !doorgestuurd) && <Typography component="div" sx={regelSx}>{eigen || '(geen tekst)'}</Typography>}
      {doorgestuurd && (
        <Box sx={{ mt: eigen ? 1 : 0, pl: 1.5, borderLeft: '3px solid #c7d2fe' }}>
          <Typography sx={{ fontSize: 11.5, color: '#4338ca', fontWeight: 700, mb: 0.25 }}>
            ↪ Doorgestuurd · {doorgestuurd.van}{doorgestuurd.datum ? ` · ${doorgestuurd.datum}` : ''}{doorgestuurd.onderwerp ? ` · ${doorgestuurd.onderwerp}` : ''}
          </Typography>
          <Typography component="div" sx={regelSx}>{doorgestuurd.tekst || '(geen tekst)'}</Typography>
          <Inklap label="handtekening" tekst={doorgestuurd.handtekening} />
        </Box>
      )}
      {bijlagen.map((b) => (
        <Box key={b.naam} sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: '#f1f5f9', border: '1px solid #e2e8f0' }}>
          <Inklap label={`📎 Bijlage: ${b.naam}${b.inDocumenten ? ' · staat bij Documenten' : ''}`} tekst={b.tekst || '(geen tekst gevonden)'} standaardOpen={b.tekst.length < 1200} />
        </Box>
      ))}
      <Inklap label="handtekening" tekst={handtekening} />
      <Inklap label={aantalEerder > 1 ? `${aantalEerder} eerdere berichten` : 'eerder bericht'} tekst={citaat} />
    </Box>
  );
}

export default function MailNotitieTekst({ tekst, sx, onderwerp }) {
  const t = String(tekst || '');
  if (/— Uit e-mailgesprek:/.test(t)) {
    const { kop, berichten } = splitsGesprek(t);
    return (
      <Box sx={sx}>
        {kop && <Typography sx={{ fontSize: 12, color: '#64748b', mb: 0.75 }}>✉️ {kop}</Typography>}
        {berichten.map((b, i) => <Bericht key={i} {...b} />)}
      </Box>
    );
  }
  const kopMatch = t.match(/^\*\*Mail van:\*\*\s*([^\n]*)/m);
  const body = kopMatch ? t.slice(kopMatch.index + kopMatch[0].length) : t;
  return (
    <Box sx={sx}>
      {kopMatch && <Typography sx={{ fontSize: 12, color: '#64748b', mb: 0.75 }}>✉️ Mail van {kopMatch[1].trim()}</Typography>}
      {(() => { const { rest, bijlagen } = haalBijlagen(body); return <Bericht kopregel="" {...metTerugval(splitsMail(rest, { onderwerp }))} bijlagen={bijlagen} />; })()}
    </Box>
  );
}

export { isMailNotitie };
